import { useStacklineTheme } from '@stackline/ui';
import React, { Dispatch, SetStateAction, createContext, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { CloseIcon } from 'src/components/SvgIcons';
import { DataFormatters, useNotificationSubscriber } from 'src/hooks/useNotificationsSubscriber';
import { clearMessage } from 'src/store/modules/auth/operations';

interface ShowNotificationArgs {
  header: React.ReactNode;
  message: React.ReactNode;
}

interface NotificationContextValue {
  showNotification: (args: ShowNotificationArgs) => void;
  closeNotification: () => void;
  notificationOpen: boolean;
  setNotificationOpen: Dispatch<SetStateAction<boolean>>;
  setDataFormatters: Dispatch<SetStateAction<DataFormatters>>;
}

interface NotificationPopUpProps extends ShowNotificationArgs {
  onClose: () => void;
}

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationContext = createContext<NotificationContextValue>(null);

/**
 *
 * This component is used for a notification bottom right side - most of the time you can find the component Auth flow.
 */
const NotificationPopUp = ({ header, message, onClose }: NotificationPopUpProps) => {
  const theme = useStacklineTheme();
  return (
    <Flex
      flexDirection="column"
      borderRadius={theme.spacing.sm}
      width="328px"
      padding={theme.spacing.md}
      sx={{
        position: 'absolute',
        backgroundColor: '#fff',
        bottom: theme.spacing.lg,
        right: theme.spacing.lg
      }}
    >
      <Flex alignItems="flex-start" justifyContent="space-between" width="100%" height="28px">
        <Text variant="h4" sx={{ fontFamily: 'Geist', fontWeight: 600 }}>
          {header}
        </Text>
        <CloseIcon onClick={onClose} style={{ cursor: 'pointer', width: '12px', height: '12px' }}></CloseIcon>
      </Flex>

      <Flex marginTop="3px" fontFamily="Geist">
        <Text variant="body2" sx={{ fontFamily: 'Geist', fontWeight: 500, lineHeight: 1.465 }}>
          {message}
        </Text>
      </Flex>
    </Flex>
  );
};

const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationHeader, setNotificationHeader] = useState('Error');
  const [notificationMessage, setNotificationMessage] = useState<React.ReactNode>(null);
  const dispatch = useDispatch();

  const { setDataFormatters } = useNotificationSubscriber();

  const showNotification = useCallback(({ message, header, open }) => {
    setNotificationMessage(message);
    setNotificationHeader(header);
    setNotificationOpen(open);
  }, []);

  const closeNotification = useCallback(() => {
    // We are handling the auth error in redux.
    dispatch(clearMessage());
    setNotificationOpen(false);
  }, []);

  return (
    <NotificationContext.Provider
      value={{ showNotification, closeNotification, notificationOpen, setNotificationOpen, setDataFormatters }}
    >
      {children}
      {notificationOpen ? (
        <NotificationPopUp header={notificationHeader} message={notificationMessage} onClose={closeNotification} />
      ) : null}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
